import React from 'react';

import Image from 'next/image';
import NextLink from 'next/link';

import Box from '@mui/material/Box';
import MuiLink from '@mui/material/Link';
import Tooltip from '@mui/material/Tooltip';

import { InlineElement, DisplayConceptRef } from '@/lib/api';
import { sku_url, topic_url } from '@/lib/utils';

// Wrapper to make Next.js Link compatible with MUI Link's component prop
const LinkBehavior = React.forwardRef<HTMLAnchorElement, React.ComponentProps<typeof NextLink>>(
  function LinkBehavior(props, ref) {
    return <NextLink ref={ref} {...props} />;
  }
);

interface InlineElementsRendererProps {
  elements: InlineElement[];
  concepts: DisplayConceptRef[];
}

interface InlineElementRendererProps {
  element: InlineElement;
  concepts: DisplayConceptRef[];
}

export const InlineElementsRenderer: React.FC<InlineElementsRendererProps> = ({
  elements,
  concepts,
}) => {
  return (
    <>
      {elements.map((element, index) => (
        <InlineElementRenderer key={index} element={element} concepts={concepts} />
      ))}
    </>
  );
};

const InlineElementRenderer: React.FC<InlineElementRendererProps> = ({ element, concepts }) => {
  switch (element.type) {
    case 'text':
      return <>{element.content}</>;

    case 'concept_link': {
      const concept = concepts.find((c) => c.cref === element.cref);
      const url = concept?.is_product ? sku_url(element.cref) : topic_url(element.cref);

      const linkContent = (
        <MuiLink
          component={LinkBehavior}
          href={url}
          sx={{
            color: 'var(--mui-palette-primary-main)',
            textDecoration: 'none',
          }}
        >
          <InlineElementsRenderer elements={element.text} concepts={concepts} />
        </MuiLink>
      );

      // Compute chip for product concepts with image
      const chip =
        concept?.is_product && concept?.img_url ? (
          <Box
            component="span"
            sx={{
              width: '1.6em',
              height: '1.2em',
              display: 'inline-block',
              verticalAlign: 'text-bottom',
              borderRadius: '4px',
              overflow: 'hidden',
              ml: 0.5,
              lineHeight: 0,
            }}
          >
            <Box
              component="img"
              src={concept.img_url}
              alt=""
              sx={{
                width: '100%',
                height: '100%',
                display: 'block',
                objectFit: 'cover',
                transform: 'scale(1.1)',
              }}
            />
          </Box>
        ) : null;

      // If we have a description (Tooltip), wrap link+chip in a Box
      if (concept?.description) {
        return (
          <Tooltip title={concept.description} arrow>
            <Box component="span">
              {linkContent}
              {chip}
            </Box>
          </Tooltip>
        );
      }

      // Otherwise return link and chip as siblings
      return (
        <>
          {linkContent}
          {chip}
        </>
      );
    }

    case 'link':
      return (
        <MuiLink
          href={element.url}
          title={element.title}
          sx={{
            color: 'var(--mui-palette-primary-main)',
            textDecoration: 'none',
          }}
        >
          <InlineElementsRenderer elements={element.text} concepts={concepts} />
        </MuiLink>
      );

    case 'image':
      return (
        <Image
          src={element.url}
          alt={element.alt}
          title={element.title}
          width={0}
          height={0}
          sizes="100vw"
          style={{ width: '100%', height: 'auto' }}
        />
      );

    case 'emphasis':
      return (
        <em>
          <InlineElementsRenderer elements={element.content} concepts={concepts} />
        </em>
      );

    case 'strong':
      return (
        <strong>
          <InlineElementsRenderer elements={element.content} concepts={concepts} />
        </strong>
      );

    case 'code':
      return <code>{element.content}</code>;

    case 'line_break':
      return <br />;

    case 'soft_break':
      return <> </>;

    default:
      return null;
  }
};
